import { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import "./CodeMatrix.css";
import axiosInstance from "../../../../Config/axiosInstance";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import IconButton from "@mui/material/IconButton";
import EditNoteSharpIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CodeMatrixCreateDialog from "./CodeMatrixCreateDialog";
import CodeMatrixEditDialog from "./CodeMatrixEditDialog";
import CodeMatrixDeleteDialog from "./CodeMatrixDeleteDialog";

function CodeMatrix() {
  const [canCreate, setCanCreate] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [departments, setDepartments] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);

  // Dialog states
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const location = useLocation();

  // Permission
  useEffect(() => {
    const requiredPath = "/project_codes/code_matrix";
    const storedPermissions = JSON.parse(localStorage.getItem("permissions"));
    const permissions = storedPermissions?.pages || [];

    const safePath = (url) => {
      try {
        return url?.startsWith("/") ? url : new URL(url).pathname;
      } catch {
        return url || "";
      }
    };

    setCanCreate(
      permissions.some((page) => safePath(page.url) === requiredPath && page.can_create === true)
    );

    setCanEdit(
      permissions.some((page) => safePath(page.url) === requiredPath && page.can_edit === true)
    );

    setCanDelete(
      permissions.some((page) => safePath(page.url) === requiredPath && page.can_delete === true)
    );
  }, []);

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [codeMatrixResponse, departmentResponse] = await Promise.all([
        axiosInstance.get("/code_matrix/"),
        axiosInstance.get("/departments/"),
      ]);

      const deptList = departmentResponse.data || [];
      setDepartments(deptList);

      const departmentMap = deptList.reduce((acc, dept) => {
        acc[dept.id] = {
          name: dept.name ?? "-",
          abbreviation: dept.department_abbreviated ?? dept.abbreviation ?? "",
        };
        return acc;
      }, {});

      const sortedData = (codeMatrixResponse.data || [])
        .map((item) => {
          const dept = departmentMap[item.department] || { name: "-", abbreviation: "" };
          return {
            id: item.id,
            department_id: item.department,
            department: dept.name,
            department_abbreviated: dept.abbreviation,
            activity: item.activity || "",
            activity_abbreviated: item.activity_abbreviated || "",
          };
        })
        .sort((a, b) => b.id - a.id);

      setRows(sortedData);
    } catch (error) {
      console.error("Eroare la obținerea datelor:", error.response?.data || error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [location.pathname, refreshKey, fetchData]);

  const triggerRefresh = () => setRefreshKey((k) => k + 1);

  // Handlers
  const openEditDialog = useCallback((row) => {
    if (!canEdit) return;
    setSelectedRow(row);
    setOpenEdit(true);
  }, [canEdit]);

  const openDeleteDialog = useCallback((row) => {
    if (!canDelete) return;
    setSelectedRow(row);
    setOpenDelete(true);
  }, [canDelete]);

  //Column
  const columns = useMemo(() => {
    const base = [
      {
        field: "department",
        headerName: "Department",
        flex: 0.8,
        minWidth: 140,
        editable: false,
        display: "flex",
        renderCell: ({ value }) => (
          <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{value}</div>
        ),
      },
      {
        field: "department_abbreviated",
        headerName: "Abbreviation",
        flex: 0.8,
        minWidth: 150,
        editable: false,
        display: "flex",
        renderCell: ({ value }) => (
          <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{value}</div>
        ),
      },
      {
        field: "activity",
        headerName: "Activity",
        flex: 0.8,
        minWidth: 180,
        editable: false,
        display: "flex",
        renderCell: ({ value }) => (
          <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{value}</div>
        ),
      },
      {
        field: "activity_abbreviated",
        headerName: "Activity Abbreviation",
        flex: 1,
        minWidth: 200,
        editable: false,
        display: "flex",
        renderCell: ({ value }) => (
          <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{value}</div>
        ),
      },
    ];

    if (canEdit || canDelete) {
      base.push({
        field: "actions",
        headerName: "Actions",
        width: 140,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <>
            {canEdit && (
              <IconButton
                sx={{ color: "#1976d2" }}
                onClick={() => openEditDialog(params.row)}
              >
                <EditNoteSharpIcon />
              </IconButton>
            )}
            {canDelete && (
              <IconButton
                sx={{ color: "#d32f2f" }}
                onClick={() => openDeleteDialog(params.row)}
              >
                <DeleteIcon />
              </IconButton>
            )}
          </>
        ),
      });
    }

    return base;
  }, [canEdit, canDelete, openEditDialog, openDeleteDialog]);


  return (
    <div className="code-matrix-page">
      {canCreate && (
        <div className="add-button-matrix">
          <Button
            variant="contained"
            disabled={!canCreate}
            sx={{ textTransform: "none" }}
            style={{ float: "right", marginBottom: "15px" }}
            onClick={() => setOpenCreate(true)}
          >
            <AddIcon />
            ADD CODE MATRIX
          </Button>
        </div>
      )}

      <div className="table-container">
        <DataGrid
          id="code-matrix-table"
          loading={loading}
          rows={rows}
          columns={columns}
          getRowHeight={() => "auto"}
          pageSize={5}
          initialState={{
            sorting: {
              sortModel: [{ field: "id", sort: "desc" }],
            },
            pagination: {
              paginationModel: { pageSize: 10 },
            },
          }}
          disableRowSelectionOnClick
          sx={{
            "& .MuiDataGrid-cell": {
              whiteSpace: "normal",
              wordBreak: "break-word",
              lineHeight: "1.4",
              display: "flex",
              alignItems: "center",
            },
            "& .MuiDataGrid-columnHeaders": {
              whiteSpace: "normal",
            },
            "& .MuiDataGrid-cell:focus": {
              outline: "none",
            },
            "& .MuiDataGrid-cell:focus-within": {
              outline: "none",
            },
            "& .Mui-selected:hover": {
              backgroundColor: "transparent !important",
            },
          }}
          pageSizeOptions={[10, 20, 25]}
        />

      </div>

      {/* CREATE */}
      <CodeMatrixCreateDialog
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        departments={departments}
        onCreated={() => {
          setOpenCreate(false);
          triggerRefresh();
        }}
      />

      {/* EDIT */}
      <CodeMatrixEditDialog
        open={openEdit}
        onClose={() => {
          setOpenEdit(false);
          setSelectedRow(null);
        }}
        departments={departments}
        row={selectedRow}
        onUpdated={() => {
          setOpenEdit(false);
          setSelectedRow(null);
          triggerRefresh();
        }}
      />

      {/* DELETE */}
      <CodeMatrixDeleteDialog
        open={openDelete}
        onClose={() => {
          setOpenDelete(false);
          setSelectedRow(null);
        }}
        row={selectedRow}
        onDeleted={() => {
          setOpenDelete(false);
          setSelectedRow(null);
          triggerRefresh();
        }}
      />
    </div>
  );
}

export default CodeMatrix;
